import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SharedService } from './shared.service';
import { AngularFireAuth } from '@angular/fire/compat/auth';

interface InitiateResponse {
  url: string;
  state: string;
}

interface CallbackResponse {
  customToken?: string;
  success?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class XeroAuthService {
  private readonly baseUrl = `${environment.functionsRootUrl}/xero-xeroAuth`;

  constructor(
    private http: HttpClient,
    private sharedService: SharedService,
    private afAuth: AngularFireAuth,
  ) {}

  async initiateXeroConnect(): Promise<void> {
    const user = await this.sharedService.getUser();

    if (!user?.uid) {
      throw new Error('User must be logged in to connect account');
    }

    const response = await this.makeRequest<InitiateResponse>('initiate-connect', { userUid: user.uid });
    this.redirectToXero(response.url);
  }

  async initiateXeroSignIn(): Promise<void> {
    const response = await this.makeRequest<InitiateResponse>('initiate-signin', {});
    this.redirectToXero(response.url);
  }

  async handleCallback(code: string, state: string) {
    const response = await this.makeRequest<CallbackResponse>('callback', { code, state });

    if (response.customToken) {
      return await this.signInWithCustomToken(response.customToken);
    }

    if (response.success) {
      console.log('Xero account connected successfully');
      return;
    }

    throw new Error('Invalid response received from callback endpoint');
  }

  async refreshToken(userUid: string): Promise<void> {
    await this.makeRequest<void>('refresh-token', { userUid });
  }

  async disconnect(userUid: string): Promise<void> {
    await this.makeRequest<void>('disconnect', { userUid });
  }

  private async makeRequest<T>(endpoint: string, body: any): Promise<T> {
    try {
      const url = `${this.baseUrl}/${endpoint}`;
      return await this.http.post<T>(url, body).pipe(take(1)).toPromise();
    } catch (error) {
      console.error(`Xero ${endpoint} failed:`, error);
      throw error;
    }
  }

  private redirectToXero(url: string): void {
    if (!url) {
      throw new Error('No redirect URL received from Xero');
    }
    window.location.href = url;
  }

  private async signInWithCustomToken(customToken: string) {
    const userCredential = await this.afAuth.signInWithCustomToken(customToken);
    console.log('Firebase sign-in successful via Xero custom token');
    return userCredential;
  }
}
